import { getGrooveUrl, getOneDriveUrl } from "client/Api/Documents/SharePoint/sharePointAction";
import { SyncClient } from "client/Api/SharePointSettings/sharepointSettings";
import {
	Anchor,
	Button,
	CopyControl,
	Dialog,
	Form,
	FormattedText,
	FormItem,
	renderReactDialog,
	Space
} from "client/Components";
import {
	AlertManager,
	useAlerts,
	useStableImperativeCallback
} from "client/Hooks";
import { DocumentsViewModel } from "client/Api/Documents/SharePoint/common";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import { navigateTo } from "client_legacy/ndaw/navigator";
import {
	ReactElement,
	useCallback,
	useEffect,
	useState
} from "react";
import { EMPTY_ARRAY } from "client/Misc/utils";
import { convertToLocationModel } from "client_legacy/Documents/SharePoint/listLocationModelProvider";

type SyncWithOneDriveDialogProps = {
	syncClient: Exclude<SyncClient, SyncClient.None>;
	view: DocumentsViewModel;
	folderId: number | nil;
	onClose: () => void;
};

const SyncWithOneDriveDialog = (props: SyncWithOneDriveDialogProps): ReactElement => {
	const {
		syncClient,
		view,
		folderId,
		onClose: onCloseFromProps
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(true);
	const [url, setUrl] = useState<string | null>(null);
	const [isInManualMode, setIsInManualMode] = useState<boolean>(false);

	const onClose: () => void = useStableImperativeCallback(onCloseFromProps);
	const alertManager: AlertManager = useAlerts();

	useEffect(
		(): (() => void) => {
			const load = async (signal: AbortSignal): Promise<void> => {
				setIsBusy(true);
				try {
					let url: string;
					switch (syncClient) {
						case SyncClient.Groove:
							url = await getGrooveUrl(
								convertToLocationModel(view.location),
								signal
							);
							break;
						case SyncClient.OneDrive:
							url = await getOneDriveUrl(
								convertToLocationModel(view.location),
								folderId,
								signal
							);
							break;
						default:
							never(syncClient);
					}

					setUrl(url);
					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					onClose();
				}
			};

			const cancellation = new AbortController();
			load(cancellation.signal);
			return (): void => cancellation.abort();
		},
		[
			alertManager,
			folderId,
			onClose,
			syncClient,
			view.location
		]
	);

	return (
		<Dialog
			trackingId="sync-with-one-drive-dialog"
			hasBusyIndicator={isBusy}
		>
			<Dialog.Header
				title={localize("_Sync")}
				onClose={onClose}
			/>

			<Dialog.Body>
				{isInManualMode
					? (
						<ManualMode view={view} />
					)
					: (
						<AutomaticMode
							url={url}
							syncClient={syncClient}
							onSwitchToManualModel={() => setIsInManualMode(true)}
						/>
					)
				}
			</Dialog.Body>

			<Dialog.Footer />
		</Dialog>
	);
};
SyncWithOneDriveDialog.displayName = "SyncWithOneDriveDialog";

type AutomaticModeProps = {
	url: string | null;
	syncClient: Exclude<SyncClient, SyncClient.None>;
	onSwitchToManualModel: () => void;
};

const AutomaticMode = (props: AutomaticModeProps): ReactElement => {
	const {
		url,
		syncClient,
		onSwitchToManualModel
	} = props;

	const syncHandler = useCallback(
		(): void => {
			if (url) {
				navigateTo(url);
			}
		},
		[url]
	);

	const renderExplanation = (): ReactElement => {
		switch (syncClient) {
			case SyncClient.Groove:
				return (
					<FormattedText
						text={localize("_SyncLibraryToYourDeviceOrClickHereToSyncManually")}
						thingsToReplace={EMPTY_ARRAY}
						thingsToWrap={[
							{
								blockId: "ClickHere",
								wrap: (children) => (
									<Anchor
										trackingId="sync-manually"
										onClick={() => onSwitchToManualModel()}
									>
										{children}
									</Anchor>
								)
							}
						]}
					/>
				);
			case SyncClient.OneDrive:
				return (
					<>
						{localize("_SyncLibraryToYourDevice")}.
					</>
				);
			default:
				never(syncClient);
		}
	};

	return (
		<Form onSubmit="NONE">
			<p>
				{renderExplanation()}
			</p>
			<FormItem>
				<span>
					<Button
						kind="secondary"
						trackingId="sync-now"
						icon="arrows-rotate"
						text={localize("_SyncNow")}
						onClick={syncHandler}
					/>
				</span>
			</FormItem>

			{/* The "old" client is only available through installing Office 2013/2016 */}
			{syncClient === SyncClient.OneDrive && (
				<p>
					{localize("_OneDriveNotInstalled")}
					<Space />
					<Anchor
						trackingId="download-one-drive"
						href={localize("_OneDriveDownloadUrl")}
						target="_blank"
					>
						{localize("_ClickHere")}
					</Anchor>
				</p>
			)}
		</Form>
	);
};
AutomaticMode.displayName = "AutomaticMode";

type ManualModeProps = {
	view: DocumentsViewModel;
};

const ManualMode = (props: ManualModeProps): ReactElement => {
	const {
		view
	} = props;

	return (
		<Form onSubmit="NONE">
			<p>
				{localize("_SyncOneDriveManualInstruction")}
			</p>
			<FormItem>
				<CopyControl
					trackingId="library-url"
					value={view.absoluteUrl}
					buttonKind="primary"
				/>
			</FormItem>
		</Form>
	);
};
ManualMode.displayName = "ManualMode";

export function openSyncWithOneDriveDialog(props: Omit<SyncWithOneDriveDialogProps, "onClose">): Promise<void> {
	return new Promise<void>(
		(resolve): void => {
			renderReactDialog(
				(onClose) => (
					<SyncWithOneDriveDialog
						{...props}
						onClose={() => {
							onClose();
							resolve();
						}}
					/>
				)
			);
		}
	);
}
